import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const AdminRevenueAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [revenueData, setRevenueData] = useState(null);
  const [gamSyncStatus, setGamSyncStatus] = useState(null);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  useEffect(() => {
    loadRealRevenueData();
    loadGAMSyncStatus();
  }, [timeRange]);

  const loadGAMSyncStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('google_ad_sync_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        setGamSyncStatus(data);
      }
    } catch (err) {
      console.error('Error loading GAM sync status:', err);
    }
  };

  const handleExportReport = () => {
    // Create CSV export of revenue data
    if (!revenueData) return;

    const csvContent = [
      ['Revenue Analytics Report', `Generated: ${new Date().toLocaleString()}`],
      [],
      ['Overview'],
      ['Total Revenue', `$${revenueData.overview.totalRevenue.toLocaleString()}`],
      ['Platform Revenue', `$${revenueData.overview.platformRevenue.toLocaleString()}`],
      ['Studio Payouts', `$${revenueData.overview.studioPayouts.toLocaleString()}`],
      [],
      ['Revenue by Source'],
      ['Source', 'Amount', 'Percentage'],
      ...revenueData.revenueBySource.map(s => [s.source, `$${s.amount.toLocaleString()}`, `${s.percentage.toFixed(1)}%`]),
      [],
      ['Top Performing Games'],
      ['Rank', 'Game', 'Studio', 'Revenue'],
      ...revenueData.topPerformingGames.map((g, i) => [i + 1, g.title, g.studio, `$${g.revenue.toLocaleString()}`]),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-analytics-${timeRange}-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const loadRealRevenueData = async () => {
    setLoading(true);

    try {
      // Calculate date range based on timeRange selection
      const endDate = new Date();
      const startDate = new Date();

      switch (timeRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case '1y':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        default:
          startDate.setDate(startDate.getDate() - 30);
      }

      // Query 1: Fetch subscription revenue (active subscriptions only)
      const { data: saasMetrics, error: saasError } = await supabase.rpc('get_saas_metrics_summary');

      if (saasError) {
        console.warn('Error fetching subscription metrics:', saasError);
      }

      const subscriptionRevenue = saasMetrics?.mrr || 0;

      // Query 2: Fetch all revenue transactions in date range
      const { data: transactions, error: transError } = await supabase
        .from('revenue_transactions')
        .select('gross_amount, platform_fee_amount, studio_revenue, transaction_date, transaction_type, game_id, studio_id')
        .gte('transaction_date', startDate.toISOString())
        .lte('transaction_date', endDate.toISOString());

      if (transError) throw transError;

      // Query 3: Revenue by game with studio info
      const { data: gameRevenue, error: gameError } = await supabase
        .from('revenue_transactions')
        .select(`
          game_id,
          gross_amount,
          games!inner(title, studio_id, gaming_studios!inner(name))
        `)
        .gte('transaction_date', startDate.toISOString())
        .lte('transaction_date', endDate.toISOString())
        .order('gross_amount', { ascending: false });

      if (gameError) throw gameError;

      // Query 3: Revenue by studio
      const { data: studioRevenue, error: studioError } = await supabase
        .from('revenue_transactions')
        .select(`
          studio_id,
          studio_revenue,
          gaming_studios!inner(name)
        `)
        .gte('transaction_date', startDate.toISOString())
        .lte('transaction_date', endDate.toISOString());

      if (studioError) throw studioError;

      // Process data
      const gameTransactionRevenue = transactions?.reduce((sum, t) => sum + parseFloat(t.gross_amount || 0), 0) || 0;
      const platformFeeFromGames = transactions?.reduce((sum, t) => sum + parseFloat(t.platform_fee_amount || 0), 0) || 0;
      const studioPayoutsTotal = transactions?.reduce((sum, t) => sum + parseFloat(t.studio_revenue || 0), 0) || 0;

      // Calculate revenue split (IAP vs Ads)
      const iapRevenue = transactions?.filter(t => t.transaction_type === 'iap')
        .reduce((sum, t) => sum + parseFloat(t.gross_amount || 0), 0) || 0;
      const adRevenue = transactions?.filter(t => t.transaction_type === 'ad_impression')
        .reduce((sum, t) => sum + parseFloat(t.gross_amount || 0), 0) || 0;

      // Total revenue includes subscriptions + game transactions
      const totalRevenue = subscriptionRevenue + gameTransactionRevenue;
      const totalPlatformRevenue = subscriptionRevenue + platformFeeFromGames; // 100% of subs + 20% of game revenue

      // Group by month for trends
      const monthlyTrends = groupByMonth(transactions || []);

      // Aggregate top games
      const topGames = aggregateByGame(gameRevenue || []);

      // Aggregate studios
      const topStudios = aggregateByStudio(studioRevenue || []);

      // Calculate revenue sources with explicit breakdown and descriptions
      const revenueBySource = [
        {
          source: 'Platform Subscriptions',
          amount: subscriptionRevenue,
          percentage: totalRevenue > 0 ? (subscriptionRevenue / totalRevenue * 100) : 0,
          description: '100% platform revenue'
        },
        {
          source: 'In-App Purchases',
          amount: iapRevenue,
          percentage: totalRevenue > 0 ? (iapRevenue / totalRevenue * 100) : 0,
          description: '20% platform / 80% studios'
        },
        {
          source: 'Ad Revenue (GAM)',
          amount: adRevenue,
          percentage: totalRevenue > 0 ? (adRevenue / totalRevenue * 100) : 0,
          description: '20% platform / 80% studios'
        }
      ];

      setRevenueData({
        overview: {
          totalRevenue,
          platformRevenue: totalPlatformRevenue,
          studioPayouts: studioPayoutsTotal,
          subscriptionRevenue,
          gameRevenue: gameTransactionRevenue,
          growth: 0, // TODO: Calculate from previous period
          avgRevenuePerUser: 0 // TODO: Calculate from user count
        },
        monthlyTrends,
        revenueBySource,
        topPerformingGames: topGames,
        studioPayouts: topStudios
      });

    } catch (error) {
      console.error('Error loading revenue data:', error);
      // Set empty data on error
      setRevenueData({
        overview: { totalRevenue: 0, platformRevenue: 0, studioPayouts: 0, subscriptionRevenue: 0, gameRevenue: 0, growth: 0, avgRevenuePerUser: 0 },
        monthlyTrends: [],
        revenueBySource: [],
        topPerformingGames: [],
        studioPayouts: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to group transactions by month
  const groupByMonth = (transactions) => {
    const monthMap = {};

    transactions.forEach(t => {
      const date = new Date(t.transaction_date);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });

      if (!monthMap[monthKey]) {
        monthMap[monthKey] = { month: monthKey, total: 0, platform: 0, studios: 0 };
      }

      monthMap[monthKey].total += parseFloat(t.gross_amount || 0);
      monthMap[monthKey].platform += parseFloat(t.platform_fee_amount || 0);
      monthMap[monthKey].studios += parseFloat(t.studio_revenue || 0);
    });

    return Object.values(monthMap).sort((a, b) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months.indexOf(a.month) - months.indexOf(b.month);
    });
  };

  // Helper function to aggregate revenue by game
  const aggregateByGame = (gameRevenue) => {
    const gameMap = {};

    gameRevenue.forEach(g => {
      const gameId = g.game_id;
      if (!gameMap[gameId]) {
        gameMap[gameId] = {
          id: gameId,
          title: g.games?.title || 'Unknown Game',
          studio: g.games?.gaming_studios?.name || 'Unknown Studio',
          revenue: 0,
          plays: 0 // TODO: Get from analytics_events
        };
      }
      gameMap[gameId].revenue += parseFloat(g.gross_amount || 0);
    });

    return Object.values(gameMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  };

  // Helper function to aggregate revenue by studio
  const aggregateByStudio = (studioRevenue) => {
    const studioMap = {};

    studioRevenue.forEach(s => {
      const studioId = s.studio_id;
      if (!studioMap[studioId]) {
        studioMap[studioId] = {
          id: studioId,
          studio: s.gaming_studios?.name || 'Unknown Studio',
          amount: 0,
          games: 0, // TODO: Count unique games
          share: 80 // 80% studio share (20% platform fee)
        };
      }
      studioMap[studioId].amount += parseFloat(s.studio_revenue || 0);
    });

    return Object.values(studioMap)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  };

  const RevenueChart = ({ data }) => (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Revenue Trends</h3>
        <Select
          value={timeRange}
          onChange={(value) => setTimeRange(value)}
          options={[
            { value: '7d', label: 'Last 7 days' },
            { value: '30d', label: 'Last 30 days' },
            { value: '90d', label: 'Last 90 days' },
            { value: '1y', label: 'Last year' }
          ]}
        />
      </div>
      
      <div className="space-y-4">
        {data.monthlyTrends.map((month, index) => (
          <div key={month.month} className="flex items-center space-x-4">
            <div className="w-12 text-sm text-muted-foreground font-medium">
              {month.month}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-foreground">Total: ${month.total.toLocaleString()}</span>
                <span className="text-xs text-muted-foreground">
                  Platform: ${month.platform.toLocaleString()} | Studios: ${month.studios.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary rounded-full h-2 transition-all duration-300"
                  style={{ width: `${(month.total / Math.max(...data.monthlyTrends.map(m => m.total))) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const RevenueSourceChart = ({ data }) => (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="text-lg font-semibold text-foreground mb-6">Revenue by Source</h3>
      
      <div className="space-y-4">
        {data.revenueBySource.map((source, index) => (
          <div key={source.source} className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-foreground">{source.source}</span>
                {source.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{source.description}</p>
                )}
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-foreground">${source.amount.toLocaleString()}</span>
                <span className="text-xs text-muted-foreground ml-2">({source.percentage.toFixed(1)}%)</span>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`rounded-full h-2 transition-all duration-300 ${
                  index === 0 ? 'bg-primary' :
                  index === 1 ? 'bg-secondary' : 'bg-accent'
                }`}
                style={{ width: `${source.percentage}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminSidebar />
        <div className="lg:pl-72">
          <div className="p-4 lg:p-8 pt-20 lg:pt-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-32 bg-muted rounded-lg"></div>
                ))}
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="h-96 bg-muted rounded-lg"></div>
                <div className="h-96 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">Revenue Analytics</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Revenue Analytics
              </h1>
              <p className="text-muted-foreground">
                Platform revenue insights and financial performance
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={handleExportReport}
              >
                Export Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Calendar"
                onClick={() => setShowCustomRange(true)}
              >
                Custom Range
              </Button>
            </div>
          </div>

          {/* Google Ad Manager Sync Status */}
          {gamSyncStatus && (
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Google Ad Manager Sync</h3>
                    <p className="text-sm text-muted-foreground">
                      Last synced: {new Date(gamSyncStatus.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-foreground">
                    ${(gamSyncStatus.total_revenue_difference || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">Revenue synced from GAM</p>
                </div>
              </div>
            </div>
          )}

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.overview.totalRevenue.toLocaleString()}
                  </p>
                  <p className="text-sm text-accent">+{revenueData.overview.growth}% from last month</p>
                </div>
                <Icon name="DollarSign" size={24} className="text-primary" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Platform Revenue</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.overview.platformRevenue.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">20% platform share</p>
                </div>
                <Icon name="TrendingUp" size={24} className="text-accent" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Studio Payouts</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.overview.studioPayouts.toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">80% to studios</p>
                </div>
                <Icon name="Building" size={24} className="text-secondary" />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Revenue/User</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${revenueData.overview.avgRevenuePerUser}
                  </p>
                  <p className="text-sm text-muted-foreground">Per active user</p>
                </div>
                <Icon name="Users" size={24} className="text-accent" />
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            <RevenueChart data={revenueData} />
            <RevenueSourceChart data={revenueData} />
          </div>

          {/* Top Performing Games */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground">Top Performing Games</h3>
                <Button variant="ghost" size="sm" iconName="ExternalLink" />
              </div>
              
              <div className="space-y-4">
                {revenueData.topPerformingGames.map((game, index) => (
                  <div key={game.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/20">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-primary">#{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{game.title}</p>
                      <p className="text-sm text-muted-foreground">{game.studio}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-foreground">${game.revenue.toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">{game.plays} plays</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Studio Payouts */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground">Studio Payouts</h3>
                <Button variant="ghost" size="sm" iconName="ExternalLink" />
              </div>
              
              <div className="space-y-4">
                {revenueData.studioPayouts.map((studio) => (
                  <div key={studio.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/20">
                    <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Building" size={16} className="text-secondary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{studio.studio}</p>
                      <p className="text-sm text-muted-foreground">
                        {studio.games} games • {studio.share}% share
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-foreground">${studio.amount.toLocaleString()}</p>
                      <p className="text-sm text-accent">Pending payout</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Revenue Breakdown</h3>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Filter"
                  onClick={() => setShowFilterModal(true)}
                >
                  Filter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={handleExportReport}
                >
                  Export
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              <div className="text-center p-4 border border-border rounded-lg">
                <Icon name="CreditCard" size={32} className="text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">
                  ${revenueData.overview.subscriptionRevenue.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Subscription Revenue</p>
                <p className="text-xs text-accent mt-1">77.5% of total</p>
              </div>
              
              <div className="text-center p-4 border border-border rounded-lg">
                <Icon name="Gamepad2" size={32} className="text-secondary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">
                  ${revenueData.overview.gameRevenue.toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Game Revenue</p>
                <p className="text-xs text-accent mt-1">22.5% of total</p>
              </div>
              
              <div className="text-center p-4 border border-border rounded-lg">
                <Icon name="TrendingUp" size={32} className="text-accent mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">
                  ${(revenueData.overview.totalRevenue / 6).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Monthly Average</p>
                <p className="text-xs text-accent mt-1">Last 6 months</p>
              </div>
              
              <div className="text-center p-4 border border-border rounded-lg">
                <Icon name="Target" size={32} className="text-primary mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">
                  ${(revenueData.overview.totalRevenue * 1.2).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">Projected Next Month</p>
                <p className="text-xs text-accent mt-1">+20% growth target</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminRevenueAnalytics;
